import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, IndianRupee, User, Calendar, Save, X } from 'lucide-react';
import SummaryApi from '../../common';
import fetchWithAuth from '../../common/fetchWithAuth';

export default function BhishiGroupDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [group, setGroup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [banks, setBanks] = useState([]);

    // Add Detail Form State
    const [detailForm, setDetailForm] = useState({
        detailDate: '',
        pukar: '',
        bharna: '',
        commission: '',
        paymentMode: '', // Initialize empty to force selection or default to Cash if preferred, user example had 'select payment mode'
        bankId: '',
        referenceNo: '',
        status: ''
    });

    const fetchBanks = async () => {
        try {
            const response = await fetchWithAuth(SummaryApi.getAllBanks.url, {
                method: SummaryApi.getAllBanks.method,
            });
            const data = await response.json();
            setBanks(data.data || []);
        } catch (error) {
            console.error("Error fetching banks:", error);
        }
    };

    const fetchGroupDetails = async () => {
        try {
            const response = await fetchWithAuth(SummaryApi.getBhishiGroupDetails.url(id), {
                method: SummaryApi.getBhishiGroupDetails.method,
            });
            const data = await response.json();
            setGroup(data);
        } catch (error) {
            console.error("Error fetching details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGroupDetails();
        fetchBanks();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setDetailForm(prev => ({ ...prev, [name]: value }));
    };

    const handleAddDetail = async (e) => {
        e.preventDefault();
        try {
            // Validate Bank Selection
            if (detailForm.paymentMode !== 'Cash' && !detailForm.bankId) {
                alert("Please select a bank account for this payment mode.");
                return;
            }

            // Validate Bank Balance
            if (detailForm.bankId) {
                const selectedBank = banks.find(b => b._id === detailForm.bankId);
                if (selectedBank && selectedBank.balance < Number(detailForm.bharna)) {
                    alert(`Insufficient Bank Balance! Available: ₹${selectedBank.balance}`);
                    return;
                }
            }

            const response = await fetchWithAuth(SummaryApi.addBhishiDetail.url, {
                method: SummaryApi.addBhishiDetail.method,
                body: JSON.stringify({ ...detailForm, groupId: id })
            });
            const data = await response.json();
            if (data.success) {
                setShowAddModal(false);
                setDetailForm({
                    detailDate: '',
                    pukar: '',
                    bharna: '',
                    commission: '',
                    paymentMode: '',
                    bankId: '',
                    referenceNo: '',
                    status: ''
                });
                fetchGroupDetails();
            } else {
                alert(data.message || "Failed to add detail");
            }
        } catch (error) {
            console.error("Error adding detail:", error);
        }
    };

    const handleAction = async (detailId, type) => {
        try {
            const response = await fetchWithAuth(SummaryApi.updateBhishiPayment.url, {
                method: SummaryApi.updateBhishiPayment.method,
                body: JSON.stringify({
                    groupId: id,
                    detailId,
                    type,
                    payeeName: type === 'Self' ? group.ownerName : 'Other'
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchGroupDetails();
            } else {
                alert("Failed to update status");
            }
        } catch (error) {
            console.error("Error updating status:", error);
        }
    };

    if (loading) return <div className="p-10 text-center">Loading...</div>;
    if (!group) return <div className="p-10 text-center text-red-500">Group not found</div>;

    return (
        <div className="w-full pb-10">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => navigate('/dashboard/bhishi')}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <ArrowLeft size={24} className="text-gray-600" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        {group.ownerName}
                        <span className="text-sm font-normal text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            {group.membersCount} Members
                        </span>
                    </h1>
                </div>
                <button
                    onClick={() => {
                        setDetailForm(prev => ({ ...prev, detailDate: new Date().toISOString().split('T')[0] }));
                        setShowAddModal(true);
                    }}
                    className="ml-auto flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl font-medium shadow-lg shadow-blue-200"
                >
                    <Plus size={18} />
                    Add Bhishi Details
                </button>
            </div>

            {/* Details Table */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Pukar</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Bharna Amt</th>
                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Commission</th>
                            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase">Action / Uchal Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {group.details && group.details.length > 0 ? (
                            [...group.details]
                                .sort((a, b) => new Date(b.detailDate) - new Date(a.detailDate))
                                .map((item) => (
                                    <tr key={item._id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-6 py-4 text-sm text-gray-700">
                                            {new Date(item.detailDate).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                            ₹{item.pukar.toLocaleString('INR')}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-emerald-600">
                                            ₹{item.bharna.toLocaleString('INR')}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-green-600">
                                            ₹{item.commission.toLocaleString('INR')}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            {item.uchalStatus && item.uchalStatus.statusType ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
                                                    <User size={12} />
                                                    {item.uchalStatus.statusType === 'Self' ? 'Self' : 'Other'}
                                                </span>
                                            ) : (
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => handleAction(item._id, 'Self')}
                                                        className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-xs font-medium"
                                                    >
                                                        Self
                                                    </button>
                                                    <button
                                                        onClick={() => handleAction(item._id, 'Other')}
                                                        className="px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors text-xs font-medium"
                                                    >
                                                        Other
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="px-6 py-8 text-center text-gray-400 text-sm">
                                    No details added yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add Detail Modal */}
            {showAddModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95">
                        <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex justify-between items-center z-10">
                            <h3 className="text-xl font-bold text-gray-800">Add Bhishi Detail</h3>
                            <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleAddDetail} className="p-6 space-y-6">

                            {/* Section 1: Bhishi Info */}
                            <div>
                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 border-b pb-2">Bhishi Information</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    {/* Date */}
                                    <div className="flex flex-col gap-1">
                                        <label className="text-sm font-semibold text-gray-700">Date <span className="text-red-500">*</span></label>
                                        <input
                                            required
                                            type="date"
                                            name="detailDate"
                                            value={detailForm.detailDate}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                        />
                                    </div>

                                    {/* Pukar */}
                                    <div className="flex flex-col gap-1">
                                        <label className="text-sm font-semibold text-gray-700">Pukar Amount <span className="text-red-500">*</span></label>
                                        <input
                                            required
                                            type="number"
                                            name="pukar"
                                            placeholder="0.00"
                                            value={detailForm.pukar}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                        />
                                    </div>

                                    {/* Bharna */}
                                    <div className="flex flex-col gap-1">
                                        <label className="text-sm font-semibold text-gray-700">Bharna Amount <span className="text-red-500">*</span></label>
                                        <input
                                            required
                                            type="number"
                                            name="bharna"
                                            placeholder="0.00"
                                            value={detailForm.bharna}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                        />
                                    </div>

                                    {/* Commission */}
                                    <div className="flex flex-col gap-1">
                                        <label className="text-sm font-semibold text-gray-700">Commission <span className="text-red-500">*</span></label>
                                        <input
                                            required
                                            type="number"
                                            name="commission"
                                            placeholder="0.00"
                                            value={detailForm.commission}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Payment Details */}
                            <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">Payment Details</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                                    {/* Payment Mode */}
                                    <div className="flex flex-col gap-1">
                                        <label className="text-sm font-medium text-gray-700">Payment Mode <span className="text-red-500">*</span></label>
                                        <select
                                            name="paymentMode"
                                            required
                                            value={detailForm.paymentMode}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                                        >
                                            <option value="" disabled hidden>select payment mode</option>
                                            <option value="Cash">Cash 💵</option>
                                            <option value="UPI">UPI / Online 📱</option>
                                            <option value="Check">Check 🏦</option>
                                        </select>
                                    </div>

                                    {/* Bank Selection (Always Enabled) */}
                                    <div className="flex flex-col gap-1">
                                        <label className="text-sm font-medium text-gray-700">Select Bank Account {detailForm.paymentMode !== 'Cash' && <span className="text-red-500">*</span>}</label>
                                        <select
                                            name="bankId"
                                            value={detailForm.bankId}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                                            required={detailForm.paymentMode !== 'Cash' && detailForm.paymentMode !== ''}
                                        >
                                            <option value="">Select Bank</option>
                                            {banks.map((bank) => (
                                                <option key={bank._id} value={bank._id}>
                                                    {bank.accountName} - {bank.accountNumber && bank.accountNumber.slice(-4)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Reference No (Conditional) */}
                                    {detailForm.paymentMode !== 'Cash' && detailForm.paymentMode !== '' && (
                                        <div className="flex flex-col gap-1">
                                            <label className="text-sm font-medium text-gray-700">Reference / Check No</label>
                                            <input
                                                type="text"
                                                name="referenceNo"
                                                value={detailForm.referenceNo}
                                                onChange={handleChange}
                                                placeholder="e.g. UPI Ref ID or Check No"
                                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                                            />
                                        </div>
                                    )}

                                    {/* Payment Status */}
                                    <div className="flex flex-col gap-1">
                                        <label className="text-sm font-medium text-gray-700">Payment Status</label>
                                        <select
                                            name="status"
                                            value={detailForm.status}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                                        >
                                            <option value="" disabled hidden>select payment status</option>
                                            <option value="Completed">Completed ✅</option>
                                            <option value="Pending">Pending ⏳</option>
                                        </select>
                                    </div>

                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-black shadow-sm transition-all transform active:scale-95 flex items-center gap-2"
                                >
                                    <Save size={18} />
                                    Save Details
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
